import { BreadcrumbLink } from "@chakra-ui/react";
import {
  LoaderFunctionArgs,
  Navigate,
  Params,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import AppLayout from "../components/layout/AppLayout";
import i18n from "../i18n";
import ForbiddenPage from "../pages/common/ForbiddenPage";
import HomeworkPage from "../pages/common/HomeworkPage";
import LoginPage from "../pages/common/LoginPage";
import NotFoundPage from "../pages/common/NotFoundPage";
import QuizSubmissionPage from "../pages/common/QuizSubmissionPage";
import SingUpPage from "../pages/common/SignUpPage";
import StudentLearningPathsPage from "../pages/student/LearningPathsPage";
import ProgressesPage from "../pages/student/ProgressesPage";
import EditLearningPathPage from "../pages/teacher/EditLearningPathPage";
import EditLessonPage from "../pages/teacher/EditLessonPage";
import EditQuestionPage from "../pages/teacher/EditQuestionPage";
import EditQuizPage from "../pages/teacher/EditQuizPage";
import EditVideoPage from "../pages/teacher/EditVideoPage";
import EnrollmentsPage from "../pages/teacher/EnrollmentsPage";
import LearningPathProgressesPage from "../pages/teacher/LearningPathProgressesPage";
import TeacherLearningPathsPage from "../pages/teacher/LearningPathsPage";
import NewExercisePage from "../pages/teacher/NewExercisePage";
import NewLearningPathPage from "../pages/teacher/NewLearningPathPage";
import NewLessonPage from "../pages/teacher/NewLessonPage";
import NewQuestionPage from "../pages/teacher/NewQuestionPage";
import NewQuizPage from "../pages/teacher/NewQuizPage";
import NewVideoPage from "../pages/teacher/NewVideoPage";
import ServiceSelectionPage from "../pages/teacher/ServiceSelectionPage";
import VideosPage from "../pages/teacher/VideosPage";
import EditExercisePage from "./../pages/teacher/EditExercisePage";
import RoleGuard from "./guards/RoleGuard";
import PreAuthLayout from "../components/layout/PreAuthLayout";

const loader: ({
  params,
}: LoaderFunctionArgs<unknown>) => Promise<Params<string>> = ({ params }) =>
  Promise.resolve(params);

const renderCrumb: (label: string, href: string) => JSX.Element = (
  label,
  href,
) => <BreadcrumbLink href={href}>{label}</BreadcrumbLink>;

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<PreAuthLayout />}>
        <Route index element={<LoginPage />} />
        <Route path="sign-up" element={<SingUpPage />} />
      </Route>
      <Route element={<AppLayout />}>
        {/* STUDENT ROUTES */}
        <Route
          path="student"
          element={<RoleGuard authorizedRole="ROLE_STUDENT" />}
        >
          <Route index element={<Navigate to="/student/learning-paths" />} />
          <Route path="learning-paths">
            <Route index element={<StudentLearningPathsPage />} />
            <Route path=":learningPathId">
              <Route index element={<ProgressesPage />} />
              <Route path="homeworks">
                <Route index path=":homeworkId" element={<HomeworkPage />} />
              </Route>
              <Route path="quizzes">
                <Route index path=":quizId" element={<QuizSubmissionPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
        {/* END STUDENT ROUTES */}
        {/* TEACHER ROUTES */}
        <Route
          path="teacher"
          element={<RoleGuard authorizedRole="ROLE_TEACHER" />}
        >
          <Route index element={<ServiceSelectionPage />} />
          <Route
            path="learning-paths"
            handle={{
              crumb: () =>
                renderCrumb(
                  i18n.t("common.labels.learning_paths"),
                  `/teacher/learning-paths`,
                ),
            }}
          >
            <Route index element={<TeacherLearningPathsPage />} />
            <Route path="create" element={<NewLearningPathPage />} />
            <Route
              path=":learningPathId"
              loader={loader}
              handle={{
                crumb: ({ learningPathId }: { learningPathId: string }) =>
                  renderCrumb(
                    `${i18n.t("common.labels.learning_path")} ${learningPathId}`,
                    `/teacher/learning-paths/${learningPathId}`,
                  ),
              }}
            >
              <Route index element={<EditLearningPathPage />} />
              <Route path="progresses">
                <Route index element={<LearningPathProgressesPage />} />
                <Route path="homeworks">
                  <Route index path=":homeworkId" element={<HomeworkPage />} />
                </Route>
                <Route path="quizzes">
                  <Route
                    index
                    loader={loader}
                    path=":quizId"
                    handle={{
                      crumb: ({ learningPathId }: { learningPathId: string }) =>
                        renderCrumb(
                          "Progresses",
                          `/teacher/learning-paths/${learningPathId}`,
                        ),
                    }}
                    element={<QuizSubmissionPage />}
                  />
                </Route>
              </Route>
              <Route path="lessons">
                <Route path="create" element={<NewLessonPage />} />
                <Route
                  path=":lessonId"
                  loader={loader}
                  handle={{
                    crumb: ({
                      learningPathId,
                      lessonId,
                    }: {
                      learningPathId: string;
                      lessonId: string;
                    }) =>
                      renderCrumb(
                        `${i18n.t("common.labels.lesson")} ${lessonId}`,
                        `/teacher/learning-paths/${learningPathId}/lessons/${lessonId}`,
                      ),
                  }}
                >
                  <Route index element={<EditLessonPage />} />
                  <Route path="quizzes">
                    <Route path="create" element={<NewQuizPage />} />
                    <Route
                      path=":quizId"
                      loader={loader}
                      handle={{
                        crumb: ({
                          learningPathId,
                          lessonId,
                          quizId,
                        }: {
                          learningPathId: string;
                          lessonId: string;
                          quizId: string;
                        }) =>
                          renderCrumb(
                            `${i18n.t("common.labels.quiz")} ${quizId}`,
                            `/teacher/learning-paths/${learningPathId}/lessons/${lessonId}/quizzes/${quizId}`,
                          ),
                      }}
                    >
                      <Route index element={<EditQuizPage />} />
                      <Route path="questions">
                        <Route path="create" element={<NewQuestionPage />} />
                        <Route
                          path=":questionId"
                          loader={loader}
                          element={<EditQuestionPage />}
                          handle={{
                            crumb: ({
                              learningPathId,
                              lessonId,
                              quizId,
                              questionId,
                            }: {
                              learningPathId: string;
                              lessonId: string;
                              quizId: string;
                              questionId: string;
                            }) =>
                              renderCrumb(
                                `${i18n.t("common.labels.quiz")} ${quizId}`,
                                `/teacher/learning-paths/${learningPathId}/lessons/${lessonId}/quizzes/${quizId}/questions/${questionId}`,
                              ),
                          }}
                        />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="exercises">
                    <Route path="create" element={<NewExercisePage />} />
                    <Route
                      path=":exerciseId"
                      element={<EditExercisePage />}
                      loader={loader}
                      handle={{
                        crumb: ({
                          learningPathId,
                          lessonId,
                          exerciseId,
                        }: {
                          learningPathId: string;
                          lessonId: string;
                          exerciseId: string;
                        }) =>
                          renderCrumb(
                            `${i18n.t("common.labels.exercise")} ${exerciseId}`,
                            `/teacher/learning-paths/${learningPathId}/lessons/${lessonId}/exercises/${exerciseId}`,
                          ),
                      }}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path="video">
            <Route index element={<VideosPage />} />
            <Route path="create" element={<NewVideoPage />} />
            <Route path=":videoId" element={<EditVideoPage />} />
          </Route>
          <Route path="enrollments">
            <Route index element={<EnrollmentsPage />} />
          </Route>
        </Route>
        {/* END TEACHER ROUTES */}
        {/* START ADMIN ROUTES */}
        <Route path="admin" element={<RoleGuard authorizedRole="ROLE_ADMIN" />}>
          <Route index element={<ServiceSelectionPage />} />
          <Route path="video">
            <Route index element={<VideosPage />} />
            <Route path="create" element={<NewVideoPage />} />
            <Route path=":videoId" element={<EditVideoPage />} />
          </Route>
        </Route>
        {/* END ADMIN ROUTES */}
      </Route>
      <Route element={<PreAuthLayout />}>
        <Route path="forbidden" element={<ForbiddenPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>,
  ),
);

export default router;
